import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AlumnoLayout from "@/components/AlumnoLayout";
import FileUploader, { FileTile } from "@/components/FileUploader";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Calendar, Award, Loader2, CheckCircle2 } from "lucide-react";

export default function AlumnoTareaDetalle() {
  const { id } = useParams();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [comments, setComments] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/alumno/assignments/${id}`);
      setA(r.data);
      if (r.data.my_delivery) {
        setComments(r.data.my_delivery.comments || "");
        setFiles(r.data.my_delivery.attachments || []);
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (files.length === 0) {
      toast.error("Adjunta al menos un archivo antes de enviar.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/alumno/assignments/${id}/submit`, {
        comments, attachments: files,
      });
      toast.success("Entrega enviada al instructor.");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!a) {
    return (
      <AlumnoLayout user={user}>
        <div className="text-sm text-gray-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
      </AlumnoLayout>
    );
  }

  const md = a.my_delivery;
  const canSubmit = !md || md.status === "returned_for_resubmission";

  return (
    <AlumnoLayout user={user}>
      <Link to="/app/alumno/tareas" className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-white mb-3">
        <ArrowLeft className="w-3 h-3" /> Volver
      </Link>
      <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
        <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">Tarea</p>
        <h1 className="mt-1 text-2xl font-bold text-white font-display">{a.title}</h1>
        {a.due_at && (
          <p className="mt-2 text-xs text-gray-400 inline-flex items-center gap-1">
            <Calendar className="w-3 h-3" /> Entrega: {new Date(a.due_at).toLocaleString("es-MX")} · Puntaje máx: {a.max_score}
          </p>
        )}
        {a.description && <p className="mt-3 text-sm text-gray-300 whitespace-pre-wrap">{a.description}</p>}
        {a.attachments?.length > 0 && (
          <div className="mt-4">
            <p className="text-[11px] uppercase tracking-widest text-gray-500">Material de referencia</p>
            <div className="mt-2 grid sm:grid-cols-2 gap-2">
              {a.attachments.map(f => <FileTile key={f.id || f.file_id} file={f} />)}
            </div>
          </div>
        )}
      </div>

      {md?.status === "graded" && (
        <div className="mt-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-5" data-testid="alumno-tarea-feedback">
          <p className="text-xs uppercase tracking-widest text-emerald-300 font-bold inline-flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> Calificada
          </p>
          {md.score !== null && (
            <p className="mt-1 text-2xl font-bold text-white inline-flex items-center gap-2">
              <Award className="w-5 h-5 text-emerald-300" /> {md.score}<span className="text-sm text-gray-400 font-normal">/ {a.max_score}</span>
            </p>
          )}
          {md.feedback && <p className="mt-3 text-sm text-gray-200 whitespace-pre-wrap">{md.feedback}</p>}
          {md.feedback_files?.length > 0 && (
            <div className="mt-3 grid gap-2">
              {md.feedback_files.map(f => <FileTile key={f.id || f.file_id} file={f} />)}
            </div>
          )}
        </div>
      )}

      {md?.status === "returned_for_resubmission" && (
        <div className="mt-6 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4" data-testid="alumno-tarea-resubmit-notice">
          <p className="text-xs uppercase tracking-widest text-amber-300 font-bold">Devuelta para reenvío</p>
          {md.feedback && <p className="mt-1 text-sm text-gray-200 whitespace-pre-wrap">{md.feedback}</p>}
        </div>
      )}

      <form onSubmit={submit} className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-5" data-testid="alumno-tarea-submit-form">
        <h2 className="text-sm font-bold text-white mb-2">{md ? (canSubmit ? "Reenvío" : "Tu entrega") : "Entregar"}</h2>
        {!canSubmit && md && (
          <p className="text-xs text-gray-400 mb-3">Ya entregaste esta tarea. Espera la retroalimentación del instructor.</p>
        )}
        <textarea rows={4} value={comments} onChange={(e) => setComments(e.target.value)}
                  disabled={!canSubmit}
                  data-testid="alumno-tarea-comments"
                  className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white disabled:opacity-60"
                  placeholder="Comentarios para el instructor (opcional)…" />
        <div className="mt-3 space-y-2">
          {files.map(f => (
            <FileTile key={f.id || f.file_id} file={f}
                      onRemove={canSubmit ? (x) => setFiles(p => p.filter(y => (y.id || y.file_id) !== (x.id || x.file_id))) : null} />
          ))}
          {canSubmit && (
            <FileUploader kind="assignment_delivery" resourceType="delivery" resourceId={id}
                          multiple onUploaded={(f) => setFiles(p => [...p, f])}
                          buttonLabel="Adjuntar archivo(s)"
                          hint="PDF, DOCX, XLSX, PPTX, imágenes, videos, .py, .ipynb… Máx 50 MB por archivo." />
          )}
        </div>
        {canSubmit && (
          <div className="mt-5 flex justify-end">
            <button type="submit" disabled={saving || files.length === 0}
                    data-testid="alumno-tarea-submit-btn"
                    className="px-5 py-2 rounded-md bg-[#F26A21] text-[#0B0F14] font-semibold text-sm disabled:opacity-50 inline-flex items-center gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {md ? "Reenviar" : "Entregar"}
            </button>
          </div>
        )}
      </form>
    </AlumnoLayout>
  );
}
